import React from 'react';
import { PublicKey } from '@solana/web3.js';
import { BN } from '@coral-xyz/anchor';
import { TOKEN_DECIMALS, DISPLAY_DECIMALS } from '../config';

// Notify user that transaction is in progress
export function notifyWait(): void {
  const title = "Transaction in progress";
  const message = "Please wait...";
  window.toastr.remove();
  window.toastr.info(message, title, {
    progressBar: true,
    timeOut: 100000,
    extendedTimeOut: 100000
  });
}

// Notify user of error
export function notifyError(customMessage?: string): void {
  const title = "Error";
  const message = customMessage || "Transaction failed. Please refresh page and try again.";
  window.toastr.remove();
  window.toastr.error(message, title, {
    progressBar: true,
    timeOut: 10000,
    extendedTimeOut: 10000
  });
}

// Notify user of success
export function notifySuccess(customMessage?: string): void {
  const title = "Success";
  const message = customMessage || "Transaction completed.";
  window.toastr.remove();
  window.toastr.success(message, title, {
    progressBar: true,
    timeOut: 10000,
    extendedTimeOut: 10000
  });
}

// Wrapper for Solana transactions with notifications
export async function checkBackendHealth(): Promise<boolean> {
  try {
    const response = await fetch('/api/health', { signal: AbortSignal.timeout(5000) });
    if (!response.ok) return false;
    const data = await response.json();
    return data.status === 'ok';
  } catch {
    return false;
  }
}

export async function solanaProcess<T>(
  transactionFn: () => Promise<T>,
  successMessage?: string
): Promise<T | false> {
  try {
    const backendOk = await checkBackendHealth();
    if (!backendOk) {
      notifyError('Backend server is not available. Please try again later.');
      return false;
    }
    notifyWait();
    const result = await transactionFn();
    notifySuccess(successMessage);
    return result;
  } catch (e) {
    console.error(e);
    const errorMessage = e instanceof Error ? e.message : String(e);
    notifyError(errorMessage);
    return false;
  }
}

// Format BN with decimals for display
export function formatBN(value: BN, decimals: number = TOKEN_DECIMALS): string {
  const divisor = new BN(10).pow(new BN(decimals - DISPLAY_DECIMALS));
  const divided = value.div(divisor);
  return (divided.toNumber() / Math.pow(10, DISPLAY_DECIMALS)).toFixed(DISPLAY_DECIMALS);
}

// Format BN to full number (no decimals shown, but accounts for token decimals)
export function formatBNFull(value: BN, decimals: number = TOKEN_DECIMALS): number {
  const divisor = new BN(10).pow(new BN(decimals));
  return value.div(divisor).toNumber();
}

// Convert display value to BN with decimals
export function toBN(value: number | string, decimals: number = TOKEN_DECIMALS): BN {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  const multiplied = numValue * Math.pow(10, decimals);
  return new BN(Math.floor(multiplied));
}

// Legacy floorBN function (for backwards compatibility)
export function floorBN(num: string | BN | number): string {
  const bignum1 = new BN(String(num));
  const bignum2 = new BN('10000000000000000');
  return bignum1.div(bignum2).toString();
}

// Display error component (returns React element)

export function displayError(error: string): React.ReactElement {
  return React.createElement('div', { className: 'main' },
    React.createElement('div', { className: 'alert alert-danger', role: 'alert' }, error)
  );
}

// Display loading spinner (returns React element)
export const displayWait: React.ReactElement = React.createElement(
  'div',
  { className: 'spinner-border m-5', role: 'status' }
);

// Regex for integer validation
export const onlyInt = /^\d+$/i;

// Check if value is integer
export function isInt(val: string): boolean {
  return onlyInt.test(val);
}

// Test form integer value
export function testFormIntValue(val: string, count: number, isMultipleOf4: boolean = true): string {
  if (val === '') {
    return ' ';
  }

  if (!isInt(val)) {
    return "Must be integer";
  }

  if (isMultipleOf4 && parseInt(val) % 4 !== 0) {
    return "Must be a multiple of 4";
  }

  if (parseInt(val) > count) {
    return "Not enough balance";
  }

  return '';
}

// Clone hash object
export function cloneHash<T extends Record<string, unknown>>(hash: T): T {
  const newHash = {} as T;
  Object.keys(hash).forEach((key) => {
    (newHash as Record<string, unknown>)[key] = hash[key];
  });
  return newHash;
}

// Clone array
export function cloneArray<T>(array: T[]): T[] {
  return array.map((x) => x);
}

// Shorten public key for display
export function shortenPubkey(pubkey: PublicKey | string, chars: number = 4): string {
  const str = typeof pubkey === 'string' ? pubkey : pubkey.toBase58();
  return `${str.slice(0, chars)}...${str.slice(-chars)}`;
}

// Fetch signature from API (for backwards compatibility with backend)
export async function getSig(
  setSig: (sig: string) => void,
  setSigMsg: (sigMsg: string) => void
): Promise<void> {
  await fetch("/api/getsig")
    .then((res) => res.json())
    .then((data) => {
      setSig(data.sig);
      setSigMsg(data.sigMsg);
    });
}

// Count bits set in a number (for game input validation)
export function countBits(n: number): number {
  let count = 0;
  while (n) {
    count += n & 1;
    n >>= 1;
  }
  return count;
}

// Validate player input for game (must have exactly 6 bits set)
export function isValidGameInput(input: number): boolean {
  return countBits(input) === 6 && input < 65536;
}

// Convert bit positions to number (for game input)
export function positionsToInput(positions: number[]): number {
  let input = 0;
  for (const pos of positions) {
    input |= (1 << pos);
  }
  return input;
}

// Convert number to bit positions (for displaying game results)
export function inputToPositions(input: number): number[] {
  const positions: number[] = [];
  for (let i = 0; i < 16; i++) {
    if (input & (1 << i)) {
      positions.push(i);
    }
  }
  return positions;
}

// Calculate matches between two inputs
export function countMatches(input1: number, input2: number): number {
  return countBits(input1 & input2);
}
